import {
    ComponentRef,
    Directive,
    ElementRef,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewContainerRef,
} from '@angular/core';
import { ButtonComponent } from '../components/button/button.component';
import { ToastMessageService } from '../modules/notifications/services/toast-message.service';
import { copyToClipboard } from '@pf/shared-utility';
import { NotificationType, ToastType } from '@pf/shared-common';

@Directive({
    selector: '[pfCopyText]',
})
export class CopyTextDirective implements OnInit, OnDestroy {
    // Pass the text directly: <div [pfCopyText]="'My Text'"></div>
    @Input('pfCopyText') textToCopy: string | undefined = '';

    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly vcr = inject(ViewContainerRef);
    private readonly toastService = inject(ToastMessageService);
    private buttonRef!: ComponentRef<ButtonComponent>; // Sets the host to flex so items st

    ngOnInit() {
        // 1. Create and Move the button inside the host
        this.buttonRef = this.vcr.createComponent(ButtonComponent);
        this.buttonRef.setInput('buttonType', 'copy');

        const host = this.elementRef.nativeElement;
        const buttonElement = this.buttonRef.location.nativeElement;
        this.renderer.appendChild(host, buttonElement);

        // 2. Apply Host Styles
        this.renderer.setStyle(host, 'display', 'inline-flex');
        this.renderer.setStyle(host, 'align-items', 'center');
        this.renderer.setStyle(host, 'gap', '8px');
        this.renderer.setStyle(host, 'cursor', 'pointer');

        // 3. Initial Button State (Hidden)
        this.renderer.setStyle(buttonElement, 'opacity', '0');
        this.renderer.setStyle(
            buttonElement,
            'transition',
            'opacity 0.2s ease-in-out'
        );

        // 4. Button Click Logic
        this.buttonRef.location.nativeElement.addEventListener(
            'click',
            (e: Event) => {
                e.stopPropagation(); // Prevent double-triggering the host click
                this.copyText();
            }
        );
    }

    // Hover Effects
    @HostListener('mouseenter') onMouseEnter() {
        this.renderer.setStyle(
            this.buttonRef.location.nativeElement,
            'opacity',
            '1'
        );
    }

    @HostListener('mouseleave') onMouseLeave() {
        this.renderer.setStyle(
            this.buttonRef.location.nativeElement,
            'opacity',
            '0'
        );
    }

    // Clicking the text itself
    @HostListener('click') onHostClick() {
        this.copyText();
    }

    async copyText() {
        if (!this.textToCopy) return;

        const copied = await copyToClipboard(this.textToCopy);

        this.toastService.create({
            message: copied ? 'Copied!' : 'Failed to copy',
            toastType: copied ? ToastType.info : ToastType.warning,
            type: NotificationType.ToastMessage,
            persist: 1000,
        });
    }

    ngOnDestroy() {
        if (this.buttonRef) {
            this.buttonRef.destroy();
        }
    }
}
